import AirIcon from '@mui/icons-material/Air';
import VisibilityIcon from '@mui/icons-material/Visibility';
import WaterDropIcon from '@mui/icons-material/WaterDrop';
import { Stack, Typography } from '@mui/material';
import { config as AtlasConfig } from '@sofarocean/atlas';
import { TimelineContext } from '@sofarocean/atlas/contexts/TimelineContext';
import { useWeatherInfo } from '@sofarocean/atlas/contexts/WeatherContext';
import type { WeatherQuantities, WeatherVariableUnit } from '@sofarocean/atlas/dist/config';
import { RasterWeatherLayer, WeatherVector } from '@sofarocean/atlas/types';
import { useWeatherValue } from '@sofarocean/atlas/weather';
import { flatten, isNil, uniq } from 'lodash';
import { LngLat, LngLatLike } from 'mapbox-gl';
import { useContext, useMemo } from 'react';

import { currentIcon, pressureIcon, wavePeriodBulkIcon, wavesBulkIcon } from '@/assets';
import { convert } from '@/common/convert';
import { DataReadoutGrid } from '@/components/data/DataReadoutGrid';
import { Datum, QuantityType } from '@/components/data/Datum';
import { DirectionDatum } from '@/components/data/DirectionDatum';
import { IconData, IconDataProps } from '@/components/data/IconData';
import { Timestamp } from '@/components/data/Timestamp';
import { INSPECTOR_READOUT_GRID_SX } from '@/components/map/inspectors/SpotterInspector';
import { InfoTip } from '@/components/ui/InfoTip';
import { useMapUiContext } from '@/context/MapUiContext';

const weatherVariablesForLayer: Record<RasterWeatherLayer | '', WeatherQuantities[]> = {
    '': [],
    wind: ['wind', 'windGust'],
    windGust: ['wind', 'windGust'],
    combinedWaves: ['combinedWaves', 'peakWaveFrequency'],
    currents: ['currents'],
    barometricPressure: ['barometricPressure'],
    precipitation: ['precipitation'],
    visibility: ['visibility'],
};

const ALL_WEATHER_VARIABLES = uniq(flatten(Object.values(weatherVariablesForLayer)));

export const WeatherInspector = ({ position, allVariables }: { position: LngLatLike; allVariables?: boolean }) => {
    const { weatherLayers } = useMapUiContext();
    const { activeInitTimeByModel } = useWeatherInfo();
    const { lng, lat } = LngLat.convert(position);

    const sofarModelGenDate = activeInitTimeByModel[AtlasConfig.weatherVariables.combinedWaves.modelID];

    const weatherVariables = useMemo(
        () =>
            allVariables
                ? ALL_WEATHER_VARIABLES
                : [
                      ...weatherVariablesForLayer[weatherLayers.raster ?? ''],
                      ...(weatherLayers.vector === 'wind-barbs' ? weatherVariablesForLayer.wind : []),
                  ],
        [allVariables, weatherLayers.raster, weatherLayers.vector]
    );

    const { weatherValues } = useWeatherValue(lat, lng, weatherVariables);

    const { currentSimulatorTime } = useContext(TimelineContext);

    return (
        <Stack sx={{ minWidth: 260 }}>
            <Stack direction="row" sx={{ alignItems: 'center', gap: 1, justifyContent: 'space-between' }}>
                <Typography variant="h4">Sofar Weather</Typography>
                <Typography variant="minorLabel">
                    Predicted for <Timestamp value={currentSimulatorTime} type="short" />
                </Typography>
            </Stack>
            <DataReadoutGrid sx={INSPECTOR_READOUT_GRID_SX} columns="auto">
                <WeatherValueReadout
                    src={wavesBulkIcon}
                    label="Waves"
                    value={weatherValues.combinedWaves}
                    type="distance"
                    directionType="source"
                    period={frequencyToPeriod(weatherValues.peakWaveFrequency)}
                />
                <WeatherValueReadout
                    src={currentIcon}
                    label="Current"
                    value={weatherValues.currents}
                    type="speed"
                    directionType="flow"
                />
                <WeatherValueReadout
                    src={pressureIcon}
                    label="Air pressure"
                    value={weatherValues.barometricPressure}
                    type="pressure"
                />
                <WeatherValueReadout
                    Icon={AirIcon}
                    label="Wind"
                    value={weatherValues.wind}
                    type="speed"
                    directionType="source"
                />
                <WeatherValueReadout
                    Icon={AirIcon}
                    label="Gust"
                    value={weatherValues.windGust}
                    type="speed"
                    directionType="source"
                />
                <WeatherValueReadout
                    Icon={VisibilityIcon}
                    label="Visibility"
                    value={weatherValues.visibility}
                    type="measuringDistance"
                />
                <WeatherValueReadout
                    Icon={WaterDropIcon}
                    label="Precipitation"
                    value={weatherValues.precipitation}
                    type="precipitationRate"
                    showZero={!allVariables}
                />
            </DataReadoutGrid>
            {sofarModelGenDate && (
                <Typography variant="minorLabel">
                    Model init time
                    <InfoTip iconSize="inherit">
                        In weather forecasting, the <i>init time</i> is the starting time from which a forecast is
                        generated.
                    </InfoTip>
                    <Timestamp value={sofarModelGenDate} type="relative" />
                </Typography>
            )}
        </Stack>
    );
};

type WeatherVectorWithUnits = WeatherVector & { units: WeatherVariableUnit };

function frequencyToPeriod(value?: WeatherVectorWithUnits): WeatherVectorWithUnits | undefined {
    if (isNil(value)) {
        return undefined;
    }

    const { magnitude, units, direction } = value;
    if (isNil(magnitude) || isNaN(magnitude)) {
        return { magnitude: null, direction, units: 's' };
    }

    return { magnitude: 1 / convert(magnitude).from(units).to('Hz'), direction, units: 's' };
}

type WeatherValueReadoutProps = IconDataProps & {
    value: WeatherVectorWithUnits;
    type: QuantityType;
    directionType?: 'source' | 'flow';
    period?: WeatherVectorWithUnits;
    showZero?: boolean;
};

const WeatherValueReadout = ({ value, type, period, showZero, directionType, ...props }: WeatherValueReadoutProps) => {
    if ((!showZero && !value?.magnitude) || isNil(value?.magnitude)) {
        return false;
    }
    const weatherValueType = type === 'pressure' ? 'airPressure' : type;

    const { magnitude, units, direction } = value;
    const hasDirection = !!(directionType && !isNil(direction) && !isNaN(direction));
    const hasPeriod = !!(period?.magnitude && !isNaN(period.magnitude));

    return (
        <IconData {...props}>
            <Datum value={magnitude} sourceUnit={units} type={weatherValueType} />
            {hasPeriod && (
                <IconData src={wavePeriodBulkIcon}>
                    <Datum value={period.magnitude} sourceUnit={period.units} type="time" />
                </IconData>
            )}
            {hasDirection && <DirectionDatum type={directionType} heading={direction} />}
        </IconData>
    );
};
